import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, X, RotateCcw, Trophy, ArrowRight, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";

function fmtTime(s) {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, "0")}`;
}

export default function QuizRunner({ questions, onRestart, onComplete, timer = false, countdown = null }) {
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [locked, setLocked] = useState(false);
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef(Date.now());

  const q = questions[idx];
  const total = questions.length;

  useEffect(() => {
    if (done) return;
    const t = setInterval(() => {
      const e = Math.floor((Date.now() - startRef.current) / 1000);
      setElapsed(e);
      if (countdown && e >= countdown) setDone(true);
    }, 1000);
    return () => clearInterval(t);
  }, [done, countdown]);

  useEffect(() => {
    if (done && onComplete) {
      onComplete({ score, total, duration: Math.floor((Date.now() - startRef.current) / 1000) });
    }
    // eslint-disable-next-line
  }, [done]);

  const choose = (i) => {
    if (locked) return;
    setSelected(i);
    setLocked(true);
    if (i === q.correct_index) setScore((s) => s + 1);
  };

  const next = () => {
    if (idx + 1 >= total) { setDone(true); return; }
    setIdx((p) => p + 1);
    setSelected(null);
    setLocked(false);
  };

  const restart = () => {
    setIdx(0); setSelected(null); setLocked(false); setScore(0); setDone(false);
    startRef.current = Date.now(); setElapsed(0);
    onRestart && onRestart();
  };

  if (done) {
    const pct = Math.round((score / total) * 100);
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
        className="text-center py-10 space-y-4" data-testid="quiz-result"
      >
        <div className="mx-auto h-16 w-16 rounded-2xl bg-accent flex items-center justify-center">
          <Trophy className="h-8 w-8 text-primary" />
        </div>
        <h3 className="font-display text-2xl font-extrabold">{score} / {total} corrette</h3>
        <p className="text-muted-foreground">
          Hai totalizzato il {pct}%{timer ? ` in ${fmtTime(elapsed)}` : ""}. {pct >= 70 ? "Ottimo lavoro!" : "Continua a ripassare!"}
        </p>
        <Button data-testid="quiz-restart-button" onClick={restart} variant="secondary" className="rounded-xl gap-2">
          <RotateCcw className="h-4 w-4" /> Riprova
        </Button>
      </motion.div>
    );
  }

  return (
    <div className="space-y-5" data-testid="quiz-runner">
      <div className="space-y-2">
        <div className="flex justify-between text-xs text-muted-foreground font-medium">
          <span>Domanda {idx + 1} di {total}</span>
          <span className="flex items-center gap-3">
            {timer && (
              <span className={`inline-flex items-center gap-1 tabular-nums ${countdown && (countdown - elapsed) <= 60 ? "text-destructive font-semibold" : ""}`} data-testid="quiz-timer">
                <Clock className="h-3.5 w-3.5" />{fmtTime(countdown ? Math.max(0, countdown - elapsed) : elapsed)}
              </span>
            )}
            <span>Punteggio: {score}</span>
          </span>
        </div>
        <Progress value={((idx) / total) * 100} className="h-1.5" />
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={idx}
          initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.22 }}
        >
          <h3 className="font-display text-lg font-bold mb-4 leading-snug">{q.question}</h3>
          <div className="space-y-2.5">
            {q.options.map((opt, i) => {
              const isCorrect = i === q.correct_index;
              const isChosen = i === selected;
              let cls = "border-border hover:border-primary/40 hover:bg-accent/40";
              if (locked && isCorrect) cls = "border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30";
              else if (locked && isChosen && !isCorrect) cls = "border-destructive bg-destructive/10";
              return (
                <button
                  key={i}
                  data-testid={`quiz-option-${i}`}
                  onClick={() => choose(i)}
                  disabled={locked}
                  className={`w-full text-left flex items-center gap-3 px-4 py-3 rounded-xl border-2 text-sm transition-all ${cls}`}
                >
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs font-semibold">
                    {locked && isCorrect ? <Check className="h-3.5 w-3.5 text-emerald-600" /> :
                     locked && isChosen && !isCorrect ? <X className="h-3.5 w-3.5 text-destructive" /> :
                     String.fromCharCode(65 + i)}
                  </span>
                  <span>{opt}</span>
                </button>
              );
            })}
          </div>

          {locked && q.explanation && (
            <motion.div
              initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
              className="mt-4 text-sm bg-secondary rounded-xl p-4 text-muted-foreground"
              data-testid="quiz-explanation"
            >
              <strong className="text-foreground">Spiegazione: </strong>{q.explanation}
            </motion.div>
          )}

          {locked && (
            <div className="mt-5 flex justify-end">
              <Button data-testid="quiz-next-button" onClick={next} className="rounded-xl gap-2">
                {idx + 1 >= total ? "Vedi risultato" : "Prossima"} <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
